import { pageTitle } from '@palaccount/config';
import type { AccountTreeRow, AccountType } from '@palaccount/contracts';
import { ChevronDown, ChevronRight, Circle, Folder, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useCurrencyPickerCodes } from '@/features/fx/api';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerCause } from '@/lib/server-messages';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import {
  useAccountLedger,
  useAccountsTree,
  useCreateAccount,
  useDeleteAccount,
  useLoadStarterChart,
  useUpdateAccount,
  type Period,
} from './api';
import { AccountingTabs } from './accounting-tabs';
import { buildAccountTree, flattenTree, selfAndDescendants, unsigned } from './tree';

/** FIN-01 §11.1.3: the type's dot colour — asset blue, expense red, liability amber, equity violet, revenue green. */
const TYPE_DOT: Record<AccountType, string> = {
  // The dots are non-text UI graphics (3:1). amber-500 measured 2.13 and
  // emerald-500 2.47 on white, so both step to the first shade that clears 3
  // (3.20 / 3.65); FIN-01's hue names — amber, green — are unchanged.
  asset: 'text-blue-500',
  expense: 'text-red-500',
  liability: 'text-amber-600',
  equity: 'text-violet-500',
  revenue: 'text-emerald-600',
};

const ACCOUNT_TYPES: AccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];


/** FIN-01 §5d's drill-down: one block at a time (NFR-42). */
const LEDGER_PAGE_SIZE = 50;

interface FormState {
  code: string;
  name: string;
  nameAr: string;
  type: AccountType;
  parentId: string;
  postable: boolean;
  active: boolean;
  currency: string;
  description: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  nameAr: '',
  type: 'asset',
  parentId: '',
  postable: true,
  active: true,
  currency: '',
  description: '',
};

function toForm(account: AccountTreeRow): FormState {
  return {
    code: account.code,
    name: account.name,
    nameAr: account.nameAr,
    type: account.type,
    parentId: account.parentId ?? '',
    postable: account.postable,
    active: account.active,
    currency: account.currency ?? '',
    description: account.description ?? '',
  };
}

/**
 * FIN-01 — Chart of Accounts: the 380px tree with live rolled-up POSTED
 * balances (unsigned in the tree, BR-FIN-01-08) beside the detail stack —
 * identity form, period balances, the ledger drill, and the posting-rules
 * note (BR-FIN-01-13). The empty state offers Load starter chart as its only
 * button (BR-FIN-01-19).
 */
export function ChartPage() {
  const { t, i18n } = useTranslation();
  const arabic = i18n.language.startsWith('ar');

  const canEdit = useCan('accounting.edit');

  const [period, setPeriod] = useState<Period>({});
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  /*
   * The tenant's own currencies, minus the base — the empty option below is
   * the base, and one fact gets one spelling. Plus whatever this account
   * already holds, so a switched-off currency still renders.
   */
  const currencyCodes = useCurrencyPickerCodes(form.currency, { includeBase: false });

  useEffect(() => {
    document.title = pageTitle(t('coa.title'));
  }, [t]);

  const { data: treeData, isPending } = useAccountsTree(period);
  const accounts = useMemo(() => treeData?.accounts ?? [], [treeData]);
  const roots = useMemo(() => buildAccountTree(accounts), [accounts]);
  const visible = useMemo(() => flattenTree(roots, collapsed), [roots, collapsed]);

  const selected = accounts.find((account) => account.id === selectedId) ?? null;

  // Selecting fills the form; creating opens a blank one (BR-FIN-01's edit gap).
  useEffect(() => {
    if (creating) return;
    setForm(selected ? toForm(selected) : EMPTY_FORM);
    setError(null);
  }, [selected, creating]);

  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const loadStarter = useLoadStarterChart();

  // §5d's drill, one page at a time (NFR-42). The closing row below stays a
  // figure about the PERIOD on every page — it comes from the server's
  // aggregate, not from the last row of whatever page is open.
  const ledgerPaging = usePagedState(LEDGER_PAGE_SIZE, 'ledger');
  const { data: ledger } = useAccountLedger(
    selected && selected.postable ? selected.id : undefined,
    period,
    ledgerPaging.page,
    LEDGER_PAGE_SIZE,
  );
  const ledgerRange = usePageRange(ledger, LEDGER_PAGE_SIZE);

  // BR-FIN-01-04's picker half: exclude self and descendants entirely.
  const parentOptions = useMemo(() => {
    const excluded =
      selected && !creating ? selfAndDescendants(accounts, selected.id) : new Set<string>();
    return accounts.filter((account) => !excluded.has(account.id));
  }, [accounts, selected, creating]);

  const debitNormal = form.type === 'asset' || form.type === 'expense';

  /**
   * The CAUSE reaches the decision, rather than a string flattened out of it
   * one expression earlier. `throwApiError` mints an `ApiError` only from a
   * response that arrived, so the discriminator separates *the server refused*
   * — whose sentence `STATIC_MESSAGE_KEYS` already translates — from *nothing
   * came back*, which used to put the browser's own `Failed to fetch` in the
   * red strip, in the browser's language, where a sentence belongs.
   *
   * `coa.errSaveFailed` is this screen's own key and deliberately not one
   * shared with the other screens carrying the same words today: the wording
   * is allowed to diverge per screen, and a shared key would have to be split
   * before it could.
   *
   * The two rows that prove the point are ERR-ADM-06-05 against ERR-ADM-08-02: rule-coverage:ignore
   * same event, different sentences, because on one of the two screens the
   * change survives locally. They are MENTIONED here to explain the
   * constraint; neither is implemented on this screen, and the ignore mark on
   * the line above is what keeps a scanner from reading the explanation as a
   * citation. (Both are already named in code by the screens that DO implement
   * them, so the mark moves no counter either way — it is there so that stays
   * true if those screens ever change.)
   */
  const fail = (thrown: unknown) => setError(localizeServerCause(t, thrown, 'coa.errSaveFailed'));

  const save = async () => {
    setError(null);
    const body = {
      code: form.code.trim(),
      name: form.name.trim(),
      ...(form.nameAr.trim() !== '' ? { nameAr: form.nameAr.trim() } : {}),
      type: form.type,
      parentId: form.parentId === '' ? null : form.parentId,
      postable: form.postable,
      currency: form.currency === '' ? null : form.currency,
      description: form.description.trim() === '' ? null : form.description.trim(),
    };
    try {
      if (creating) {
        const made = await createAccount.mutateAsync(body);
        setCreating(false);
        setSelectedId(made.id);
      } else if (selected) {
        await updateAccount.mutateAsync({
          accountId: selected.id,
          body: { ...body, active: form.active },
        });
      }
    } catch (thrown) {
      fail(thrown);
    }
  };

  /** The Active switch acts immediately — BR-FIN-01-18 answers on the spot. */
  const toggleActive = async (next: boolean) => {
    if (!selected) return;
    setError(null);
    try {
      await updateAccount.mutateAsync({ accountId: selected.id, body: { active: next } });
    } catch (thrown) {
      fail(thrown);
    }
  };

  const remove = async () => {
    setDeleting(false);
    if (!selected) return;
    setError(null);
    try {
      await deleteAccount.mutateAsync(selected.id);
      setSelectedId(null);
    } catch (thrown) {
      fail(thrown);
    }
  };

  const toggleBranch = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startCreate = (parentId: string | null) => {
    setCreating(true);
    setForm({ ...EMPTY_FORM, parentId: parentId ?? '' });
    setError(null);
  };

  // ── The BR-FIN-01-19 empty state ──────────────────────────────────────────
  if (!isPending && accounts.length === 0) {
    return (
      <div className="grid gap-4">
        <AccountingTabs />
        <h2 className="text-lg font-bold">{t('coa.title')}</h2>
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-muted-foreground">{t('coa.emptyHint')}</p>
            <Gated permission="accounting.create" reasonKey="perm.cannot.createAccounts">
              <Button
                onClick={() => {
                  setError(null);
                  loadStarter.mutateAsync().catch(fail);
                }}
                disabled={loadStarter.isPending}
              >
                {t('coa.loadStarter')}
              </Button>
            </Gated>
            {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <AccountingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('coa.title')}</h2>
        <Gated permission="accounting.create" reasonKey="perm.cannot.createAccounts">
          <Button size="sm" onClick={() => startCreate(selected?.id ?? null)}>
            <Plus className="me-1 size-4" aria-hidden />
            {t('coa.newAccount')}
          </Button>
        </Gated>
      </div>

      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
        {/* ── The tree ─────────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('coa.treeTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[620px] overflow-y-auto p-2">
            <ul role="tree" aria-label={t('coa.treeTitle')} className="grid gap-px">
              {visible.map((node) => {
                const account = node.account;
                const hasChildren = node.children.length > 0;
                const isCollapsed = collapsed.has(account.id);
                return (
                  <li key={account.id} role="treeitem" aria-selected={account.id === selectedId}>
                    <div
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-1 rounded px-1 py-1 text-sm hover:bg-muted',
                        account.id === selectedId && 'bg-blue-50 dark:bg-blue-950',
                        !account.active && 'opacity-50',
                      )}
                      // BR-FIN-01-12: direction-aware indentation, 18px/level.
                      style={{ [arabic ? 'paddingRight' : 'paddingLeft']: node.depth * 18 + 4 }}
                      onClick={() => {
                        setCreating(false);
                        setSelectedId(account.id);
                      }}
                    >
                      {hasChildren ? (
                        <button
                          type="button"
                          aria-label={t(isCollapsed ? 'stock.expand' : 'stock.collapse')}
                          className="shrink-0 text-muted-foreground"
                          onClick={(event) => {
                            // The triangle expands ONLY (a listed prototype defect).
                            event.stopPropagation();
                            toggleBranch(account.id);
                          }}
                        >
                          {isCollapsed ? (
                            <ChevronRight className="size-4 rtl:-scale-x-100" aria-hidden />
                          ) : (
                            <ChevronDown className="size-4" aria-hidden />
                          )}
                        </button>
                      ) : (
                        <span className="size-4 shrink-0" />
                      )}
                      {account.postable ? (
                        <Circle
                          className={cn('size-3 shrink-0 fill-current', TYPE_DOT[account.type])}
                          aria-hidden
                        />
                      ) : (
                        <Folder className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      )}
                      <Ltr value={account.code} className="shrink-0 font-mono text-xs" />
                      <span
                        className={cn('min-w-0 flex-1 truncate', !account.postable && 'font-bold')}
                      >
                        {arabic ? account.nameAr : account.name}
                      </span>
                      {/* BR-FIN-01-08: unsigned in the tree. */}
                      <Amount value={unsigned(account.balance)} className="text-xs" />
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
          {/* BR-FIN-01-16's checksum, visible: the trial-balance totals. */}
          {treeData ? (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">
              {t('coa.totalDebits')} <Amount value={treeData.totalDebit} /> ·{' '}
              {t('coa.totalCredits')} <Amount value={treeData.totalCredit} />
            </div>
          ) : null}
        </Card>

        {/* ── The detail stack ─────────────────────────────────────────── */}
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {creating ? (
                  t('coa.newAccount')
                ) : selected ? (
                  <span className="flex items-center gap-2">
                    <Ltr value={selected.code} className="font-mono" />
                    {arabic ? selected.nameAr : selected.name}
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                        'bg-muted',
                        TYPE_DOT[selected.type],
                      )}
                    >
                      {t(`coa.type.${selected.type}`)}
                    </span>
                  </span>
                ) : (
                  t('coa.selectPrompt')
                )}
              </CardTitle>
            </CardHeader>
            {(creating || selected) && (
              <CardContent className="grid gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <Label htmlFor="coa-code">{t('coa.fCode')}</Label>
                    <Input
                      id="coa-code"
                      dir="ltr"
                      className="font-mono"
                      value={form.code}
                      disabled={!canEdit && !creating}
                      onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="coa-type">{t('coa.fType')}</Label>
                    <NativeSelect
                      id="coa-type"
                      value={form.type}
                      disabled={!canEdit && !creating}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, type: e.target.value as AccountType }))
                      }
                    >
                      {ACCOUNT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`coa.type.${type}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="coa-name">{t('coa.fName')}</Label>
                    <Input
                      id="coa-name"
                      value={form.name}
                      disabled={!canEdit && !creating}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="coa-name-ar">{t('coa.fNameAr')}</Label>
                    <Input
                      id="coa-name-ar"
                      dir="rtl"
                      value={form.nameAr}
                      disabled={!canEdit && !creating}
                      onChange={(e) => setForm((f) => ({ ...f, nameAr: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="coa-parent">{t('coa.fParent')}</Label>
                    <NativeSelect
                      id="coa-parent"
                      value={form.parentId}
                      disabled={!canEdit && !creating}
                      onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
                    >
                      <option value="">{t('coa.parentNone')}</option>
                      {parentOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {' '.repeat(0)}
                          {option.code} {arabic ? option.nameAr : option.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="coa-currency">{t('coa.fCurrency')}</Label>
                    <NativeSelect
                      id="coa-currency"
                      value={form.currency}
                      disabled={!canEdit && !creating}
                      onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                    >
                      {/*
                        BR-DATA-06 + BR-MD-01-07. The empty row IS the base
                        (`includeBase: false` below, so it is not offered
                        twice); the rest is the tenant's own live table plus,
                        if the account already carries a code the table no
                        longer offers, that code — a foreign account whose
                        currency was switched off used to render BLANK here,
                        which is one careless save away from the partner form's
                        silent write.
                      */}
                      <option value="">{t('coa.currencyBase')}</option>
                      {currencyCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </NativeSelect>
                  </div>
                </div>

                {/* BR-FIN-01-03: derived, never chosen — marked · auto. */}
                <p className="text-sm text-muted-foreground">
                  <span
                    className={cn(
                      'me-1 inline-block size-2.5 rounded-sm align-middle',
                      debitNormal ? 'bg-blue-500' : 'bg-amber-500',
                    )}
                    aria-hidden
                  />
                  <span className="font-medium text-foreground">
                    {t(debitNormal ? 'coa.normalDebit' : 'coa.normalCredit')}
                  </span>{' '}
                  · {t('coa.normalAuto')} —{' '}
                  {t(debitNormal ? 'coa.normalDebitHint' : 'coa.normalCreditHint')}
                </p>

                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!form.postable}
                    disabled={!canEdit && !creating}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({ ...f, postable: checked !== true }))
                    }
                  />
                  <span>
                    {t('coa.fHeader')}
                    <span className="block text-xs text-muted-foreground">
                      {t('coa.headerHint')}
                    </span>
                  </span>
                </label>

                {!creating && selected ? (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.active}
                      disabled={!canEdit}
                      onCheckedChange={(checked) => void toggleActive(checked === true)}
                    />
                    {t('coa.fActive')}
                  </label>
                ) : null}

                <div className="grid gap-1">
                  <Label htmlFor="coa-description">{t('coa.fDescription')}</Label>
                  <Textarea
                    id="coa-description"
                    rows={2}
                    value={form.description}
                    disabled={!canEdit && !creating}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Gated
                    permission={creating ? 'accounting.create' : 'accounting.edit'}
                    reasonKey={
                      creating ? 'perm.cannot.createAccounts' : 'perm.cannot.editAccounts'
                    }
                  >
                    <Button
                      size="sm"
                      onClick={() => void save()}
                      disabled={
                        form.code.trim() === '' ||
                        form.name.trim() === '' ||
                        createAccount.isPending ||
                        updateAccount.isPending
                      }
                    >
                      {t('common.save')}
                    </Button>
                  </Gated>
                  {creating ? (
                    <Button size="sm" variant="outline" onClick={() => setCreating(false)}>
                      {t('common.cancel')}
                    </Button>
                  ) : null}
                  {!creating && selected ? (
                    <Gated permission="accounting.delete" reasonKey="perm.cannot.deleteAccounts">
                      <Button size="sm" variant="outline" onClick={() => setDeleting(true)}>
                        {t('common.delete')}
                      </Button>
                    </Gated>
                  ) : null}
                  {/* GEN-58 / GEN-204 / GEN-209: the shared box, not
                      `window.confirm`, which is neither of BR-PERM-15's two
                      permitted patterns. Outside the `Gated` deliberately —
                      `Gated` takes ONE child and greys what it wraps, and a
                      greyed dialog is not a thing. */}
                  <DeleteConfirmBox
                    open={deleting}
                    detail={t('coa.confirmDelete')}
                    onCancel={() => setDeleting(false)}
                    onConfirm={() => void remove()}
                    busy={deleteAccount.isPending}
                  />
                </div>

                {/*
                  MD-RULE-5's showing half (MD-06) — all four facts at the
                  foot of the account's own card. Two honest absences ride
                  the shared component: a NULL author (the seeded chart is
                  the signup machinery's work, decision-28 seam) and a NULL
                  `updatedAt` — accounts' `updated_at` arrived late
                  (20260902100000) with no backfilled date, so "no change
                  recorded" draws nothing rather than an invented time.
                */}
                {!creating && selected ? <AuthorshipLine record={selected} /> : null}
              </CardContent>
            )}
          </Card>

          {!creating && selected ? (
            <>
              {/* ── Balances (BR-FIN-01-14: the period filters everything) ── */}
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
                  <CardTitle className="text-sm">{t('coa.balances')}</CardTitle>
                  <div className="flex items-center gap-2 text-xs">
                    <Label htmlFor="coa-from">{t('coa.periodFrom')}</Label>
                    <Input
                      id="coa-from"
                      type="date"
                      className="h-8 w-36"
                      value={period.from ?? ''}
                      onChange={(e) =>
                        setPeriod((p) => ({ ...p, from: e.target.value || undefined }))
                      }
                    />
                    <Label htmlFor="coa-to">{t('coa.periodTo')}</Label>
                    <Input
                      id="coa-to"
                      type="date"
                      className="h-8 w-36"
                      value={period.to ?? ''}
                      onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value || undefined }))}
                    />
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-3">
                  {(
                    [
                      ['coa.tileCurrent', selected.balance, true],
                      ['coa.tileDebits', selected.debit, false],
                      ['coa.tileCredits', selected.credit, false],
                    ] as const
                  ).map(([key, value, signed]) => (
                    <div key={key} className="rounded-md border p-3">
                      <div className="text-xs text-muted-foreground">{t(key)}</div>
                      {/* The DETAIL card shows the sign, in red (BR-FIN-01-08). */}
                      <Amount
                        value={value}
                        className={cn(
                          'text-base font-semibold',
                          signed && value.startsWith('-') && 'text-red-600 dark:text-red-400',
                        )}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* ── The account ledger (§5d) ────────────────────────────── */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('coa.ledger')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {!selected.postable ? (
                    <p className="py-4 text-sm text-muted-foreground">{t('coa.groupLedgerMsg')}</p>
                  ) : !ledger || ledger.rows.length === 0 ? (
                    <p className="py-4 text-sm text-muted-foreground">{t('coa.noPostings')}</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('common.date')}</TableHead>
                          <TableHead>{t('coa.colDocument')}</TableHead>
                          <TableHead>{t('coa.colReference')}</TableHead>
                          <TableHead className="text-end">{t('coa.colDebit')}</TableHead>
                          <TableHead className="text-end">{t('coa.colCredit')}</TableHead>
                          <TableHead className="text-end">{t('coa.colBalance')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledger.rows.map((row, index) => (
                          <TableRow key={`${row.entryId}-${index}`}>
                            <TableCell>
                              <Ltr value={row.entryDate} className="font-mono text-xs" />
                            </TableCell>
                            <TableCell>
                              <Link
                                to={`/accounting/journals/${row.entryId}`}
                                className="font-mono text-xs text-primary underline-offset-2 hover:underline"
                              >
                                <Ltr value={row.code} />
                              </Link>
                            </TableCell>
                            <TableCell className="max-w-48 truncate text-xs">
                              {row.lineMemo ?? row.reference ?? '—'}
                            </TableCell>
                            <TableCell className="text-end">
                              {row.debit === '0.00' ? '—' : <Amount value={row.debit} />}
                            </TableCell>
                            <TableCell className="text-end">
                              {row.credit === '0.00' ? '—' : <Amount value={row.credit} />}
                            </TableCell>
                            <TableCell className="text-end">
                              <Amount value={row.balance} />
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="font-semibold">
                          <TableCell colSpan={5}>{t('coa.closingBalance')}</TableCell>
                          <TableCell className="text-end">
                            <Amount value={ledger.closingBalance} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                  {selected.postable && ledgerRange.pages > 1 ? (
                    <div className="pt-3">
                      <Pager
                        page={ledgerPaging.page}
                        pages={ledgerRange.pages}
                        first={ledgerRange.first}
                        last={ledgerRange.last}
                        total={ledgerRange.total}
                        onPage={ledgerPaging.setPage}
                        label={t('coa.ledger')}
                      />
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* ── Posting rules live in Settings, not here (BR-FIN-01-13) ── */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('coa.postingRules')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{t('coa.postingRulesBody')}</p>
                </CardContent>
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
